import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Crown, Check, Sparkles, Zap, BarChart3, Target, Loader2,
  FileText, Shield, Infinity, Users, Copy, RefreshCw, DollarSign,
  ChevronDown, ChevronUp, CheckCircle2, XCircle, Pause, Clock,
  TrendingUp, AlertCircle, FileSignature, Settings
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const isAdmin = user?.email === "ai.manifest.official@gmail.com";
  const isPro = isAdmin || user?.subscription_tier === "pro" || user?.subscription_tier === "premium";
  const isCancelling = user?.subscription_cancel_at_period_end === true;

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API}/subscriptions/checkout`,
        { origin_url: originUrl, package_id: "pro_weekly" },
        { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      window.location.href = response.data.url;
    } catch (error) {
      toast.error("Failed to initiate checkout");
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!confirm("Are you sure you want to cancel your AURA PRO subscription? You'll keep access until the end of your current billing period.")) return;
    try {
      await axios.post(`${API}/subscriptions/cancel`, {}, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Subscription will cancel at the end of your billing period.");
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel subscription");
    }
  };

  const handleReactivate = async () => {
    try {
      await axios.post(`${API}/subscriptions/reactivate`, {}, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Subscription reactivated!");
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reactivate subscription");
    }
  };

  const handleManagePortal = async () => {
    try {
      const response = await axios.post(
        `${API}/subscriptions/customer-portal`,
        {},
        { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (response.data?.url) {
        window.location.href = response.data.url;
      } else {
        toast.error("Could not open Stripe Customer Portal");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to open Stripe Customer Portal");
    }
  };

  // Admin sees subscriber management hub
  if (isAdmin) {
    return (
      <DashboardLayout currentPage="subscription">
        <div className="p-6 max-w-6xl mx-auto space-y-6">
          <AdminSubscriptionHub token={token} />
        </div>
      </DashboardLayout>
    );
  }

  const freeFeatures = [
    "1 prediction per week",
    "Basic game tracking",
    "Limited analytics",
    "Community winnings view"
  ];

  const proFeatures = [
    { text: "Unlimited Predictions.", icon: Infinity },
    { text: "All 29 AI Analytics implemented.", icon: Target },
    { text: "Games Tracking.", icon: BarChart3 },
    { text: "Advanced Research Applied.", icon: Sparkles },
    { text: "Performance Reports.", icon: FileText },
    { text: "PDF report exports.", icon: Shield },
    { text: "Weekly and Monthly ROI reports.", icon: TrendingUp },
    { text: "No lock-in contract - cancel anytime", icon: FileSignature, accent: "emerald" }
  ];

  return (
    <DashboardLayout currentPage="subscription">
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-aura-gold/10 border border-aura-gold/20 text-aura-gold text-base font-semibold mb-4">
              <Crown className="w-4 h-4" />
              <span>Launch Special - Limited Time Only</span>
            </div>
            <p className="text-sm text-aura-gold mb-6 max-w-xl mx-auto px-4">
              Join today and lock in your 40% OFF discounted rate of only $3.50 per week while your subscription remains active - cancel anytime!
            </p>
            <h1 className="font-heading text-4xl font-bold text-white mb-4">
              Unlock <span className="gold-gradient-text">AURA PRO</span>
            </h1>
            <p className="text-xl text-aura-text-secondary max-w-xl mx-auto">
              Track Better and make informed decisions
            </p>
            {/* Accent banner */}
            <div className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-aura-emerald/10 border border-aura-emerald/20">
              <Sparkles className="w-4 h-4 text-aura-emerald" />
              <span className="text-aura-emerald text-sm font-medium">
                Play Smart. Win Consistently.
              </span>
            </div>
          </motion.div>
        </div>

        {/* Plans */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Free Plan */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="card-aura p-8">
            <div className="mb-6">
              <h3 className="font-heading text-xl font-semibold text-white mb-2">Free</h3>
              <div className="flex items-baseline gap-1">
                <span className="font-heading text-4xl font-bold text-white">$0</span>
                <span className="text-aura-text-muted">/forever</span>
              </div>
            </div>
            <ul className="space-y-4 mb-8">
              {freeFeatures.map((feature, i) => (
                <li key={i} className="flex items-center gap-3 text-aura-text-secondary">
                  <Check className="w-5 h-5 text-aura-emerald flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            <Button variant="outline" className="w-full border-white/20 text-aura-text-secondary" disabled={!isPro}>
              {isPro ? "Current Plan: PRO" : "Current Plan"}
            </Button>
          </motion.div>

          {/* PRO Plan */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
              <span className="px-4 py-1 rounded-full bg-aura-gold text-black text-xs font-bold">BEST VALUE</span>
            </div>
            <div className="card-aura p-8 border-aura-gold/30 bg-gradient-to-b from-aura-gold/5 to-transparent">
              <div className="mb-6">
                <h3 className="font-heading text-xl font-semibold text-white mb-2 flex items-center gap-2">
                  AURA PRO
                  <Crown className="w-5 h-5 text-aura-gold" />
                </h3>
                <div className="flex items-baseline gap-1">
                  <span className="font-heading text-2xl font-bold text-aura-text-muted line-through decoration-aura-text-muted decoration-1">$5.85</span>
                  <span className="font-heading text-4xl font-bold text-white">$3.50</span>
                  <span className="text-aura-text-muted">AUD/week</span>
                </div>
                <p className="text-sm text-aura-gold mt-1">Less than a coffee ☕</p>
              </div>
              <ul className="space-y-4 mb-8">
                {proFeatures.map((feature, i) => {
                  const isEmerald = feature.accent === "emerald";
                  return (
                    <li key={i} className={`flex items-center gap-3 ${isEmerald ? "text-aura-emerald" : "text-white"}`}>
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${isEmerald ? "bg-aura-emerald/20" : "bg-aura-gold/20"}`}>
                        <feature.icon className={`w-3 h-3 ${isEmerald ? "text-aura-emerald" : "text-aura-gold"}`} />
                      </div>
                      <span>{feature.text}</span>
                    </li>
                  );
                })}
              </ul>

              {isPro && !isCancelling ? (
                <div className="space-y-3">
                  <div className="text-center py-4 rounded-lg bg-aura-emerald/10 border border-aura-emerald/20">
                    <p className="text-aura-emerald font-medium flex items-center justify-center gap-2">
                      <Check className="w-5 h-5" />
                      You're an AURA PRO Member
                    </p>
                  </div>
                  {user?.stripe_customer_id && (
                    <Button
                      onClick={handleManagePortal}
                      className="w-full btn-gold py-6"
                      data-testid="manage-cancel-subscription-btn"
                    >
                      <Settings className="w-5 h-5 mr-2" />
                      Manage / Cancel Subscription
                    </Button>
                  )}
                  {user?.stripe_subscription_id && (
                    <p
                      className="w-full text-center text-xs text-aura-text-muted py-2 select-none"
                      data-testid="cancel-subscription-static-label"
                    >
                      Cancel Subscription
                    </p>
                  )}
                </div>
              ) : isCancelling ? (
                <div className="space-y-3">
                  <div className="text-center py-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                    <p className="text-yellow-400 font-medium text-sm flex items-center justify-center gap-2">
                      <Clock className="w-4 h-4" />
                      Your PRO access ends {user?.subscription_period_end ? new Date(user.subscription_period_end).toLocaleDateString() : "at end of billing period"}
                    </p>
                  </div>
                  <Button className="w-full btn-gold py-6" onClick={handleReactivate} data-testid="reactivate-subscription-btn">
                    <Sparkles className="w-5 h-5 mr-2" />
                    Keep My PRO Subscription
                  </Button>
                </div>
              ) : (
                <Button className="w-full btn-gold py-6" onClick={handleUpgrade} disabled={loading} data-testid="upgrade-pro-btn">
                  {loading ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Processing...</>
                  ) : (
                    <><Sparkles className="w-5 h-5 mr-2" />Upgrade to PRO</>
                  )}
                </Button>
              )}
            </div>
          </motion.div>
        </div>

        {/* Why Go PRO */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-12">
          <h3 className="font-heading text-xl font-semibold text-white text-center mb-8">Why Go PRO?</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="card-aura p-6 text-center">
              <div className="w-12 h-12 rounded-xl bg-aura-emerald/10 flex items-center justify-center mx-auto mb-4">
                <Target className="w-6 h-6 text-aura-emerald" />
              </div>
              <h4 className="font-medium text-white mb-2">29 AI Strategies</h4>
              <p className="text-sm text-aura-text-secondary">Access all advanced analysis methods including Monte Carlo & Markov chains</p>
            </div>
            <div className="card-aura p-6 text-center">
              <div className="w-12 h-12 rounded-xl bg-aura-gold/10 flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-6 h-6 text-aura-gold" />
              </div>
              <h4 className="font-medium text-white mb-2">Advanced Analytics</h4>
              <p className="text-sm text-aura-text-secondary">Deep insights with charts, trends, and pattern recognition</p>
            </div>
            <div className="card-aura p-6 text-center">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mx-auto mb-4">
                <FileText className="w-6 h-6 text-purple-400" />
              </div>
              <h4 className="font-medium text-white mb-2">PDF Reports</h4>
              <p className="text-sm text-aura-text-secondary">Export detailed performance reports with ROI tracking</p>
            </div>
          </div>
        </motion.div>

        <div className="mt-12 text-center">
          <p className="text-sm text-aura-text-muted">Secure payment powered by Stripe. Cancel anytime — keep access until end of billing period.</p>
        </div>
      </div>
    </DashboardLayout>
  );
};


// ════════════════════════════════════════════════════════════════
// ADMIN SUBSCRIPTION HUB — Subscriber management + Affiliate management
// ════════════════════════════════════════════════════════════════
const AdminSubscriptionHub = ({ token }) => {
  const [subData, setSubData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("paid");
  const [refreshKey, setRefreshKey] = useState(0);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    axios.get(`${API}/earn/admin/subscribers`, { withCredentials: true, headers }).then(res => {
      setSubData(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [refreshKey]);

  useEffect(() => {
    const interval = setInterval(() => setRefreshKey(k => k + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  const copyEmails = (list) => {
    const emails = list.map(u => u.email).join(", ");
    navigator.clipboard.writeText(emails);
    toast.success(`${list.length} emails copied!`);
  };

  if (loading) return <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-aura-emerald mx-auto" /></div>;

  const paidCount = subData?.total_paid ?? 0;
  const freeCount = subData?.total_free ?? 0;
  const weeklyEarnings = paidCount * 3.50;
  const list = tab === "paid" ? (subData?.paid_subscribers || []) : (subData?.free_subscribers || []);

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white mb-1">Subscription Management</h1>
          <p className="text-aura-text-secondary text-sm">Subscriber and revenue management.</p>
        </div>
        <Button size="sm" variant="outline" className="border-white/10 text-aura-text-muted" onClick={() => setRefreshKey(k => k + 1)} data-testid="admin-sub-refresh-btn">
          <RefreshCw className="w-3 h-3 mr-1" /> Refresh
        </Button>
      </div>

      {/* Live counters */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="card-aura p-4 text-center"><p className="text-2xl font-bold text-aura-emerald">{paidCount}</p><p className="text-xs text-aura-text-muted">Paid Subscribers</p></div>
        <div className="card-aura p-4 text-center"><p className="text-2xl font-bold text-white">{freeCount}</p><p className="text-xs text-aura-text-muted">Free Subscribers</p></div>
        <div className="card-aura p-4 text-center"><p className="text-2xl font-bold text-aura-gold">${weeklyEarnings.toFixed(2)}</p><p className="text-xs text-aura-text-muted">Weekly Earnings</p></div>
        <div className="card-aura p-4 text-center"><p className="text-2xl font-bold text-aura-gold">${(weeklyEarnings * 52).toFixed(2)}</p><p className="text-xs text-aura-text-muted">Yearly Forecast</p></div>
      </div>

      {/* Subscriber tables */}
      <div className="card-aura p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex gap-2">
            {["paid", "free"].map(t => (
              <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${tab === t ? "bg-aura-emerald/20 text-aura-emerald" : "text-aura-text-muted hover:text-white"}`} data-testid={`admin-tab-${t}`}>
                {t === "paid" ? `Paid (${paidCount})` : `Free (${freeCount})`}
              </button>
            ))}
          </div>
          <Button size="sm" variant="outline" className="border-white/10 text-aura-text-muted text-xs" onClick={() => copyEmails(list)} data-testid={`admin-copy-${tab}-emails`}>
            <Copy className="w-3 h-3 mr-1" /> Copy All Emails
          </Button>
        </div>
        <div className="overflow-x-auto max-h-80 overflow-y-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-aura-text-muted border-b border-white/5 sticky top-0 bg-[#0a0e14]">
              <th className="text-left py-2 px-2">Email</th>
              <th className="text-left py-2 px-2">Name</th>
              <th className="text-center py-2 px-2">Plan</th>
              <th className="text-center py-2 px-2">Status</th>
              <th className="text-left py-2 px-2">Signup</th>
            </tr></thead>
            <tbody>{list.map((u, i) => (
              <tr key={i} className="border-b border-white/5">
                <td className="py-1.5 px-2 text-white">{u.email}</td>
                <td className="py-1.5 px-2 text-aura-text-muted">{u.name || "—"}</td>
                <td className="py-1.5 px-2 text-center"><span className={`text-xs px-2 py-0.5 rounded-full ${u.plan_type === "free" ? "bg-white/5 text-aura-text-muted" : "bg-aura-emerald/20 text-aura-emerald"}`}>{u.plan_type}</span></td>
                <td className="py-1.5 px-2 text-center"><span className={`text-xs px-2 py-0.5 rounded-full ${u.status === "active" ? "bg-aura-emerald/20 text-aura-emerald" : "bg-white/5 text-aura-text-muted"}`}>{u.status}</span></td>
                <td className="py-1.5 px-2 text-aura-text-muted">{u.signup_date?.split("T")[0] || "—"}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    </>
  );
};

const SmallStat = ({ label, value }) => (
  <div className="bg-white/5 rounded-lg p-3 text-center">
    <p className="text-lg font-semibold text-white">{value}</p>
    <p className="text-xs text-aura-text-muted">{label}</p>
  </div>
);

export default SubscriptionPage;
